import { useState } from 'react'
import { Button } from './Button'
import { V2Icon } from './v2'

/**
 * Shared analysis result card. Renders the polished app-style "Your Kidney
 * Analysis" view — used by both DemoScanStep (canned demo data) and
 * UserScanStep (real cloud-function response). Visually mirrors the iOS app's
 * meal analysis screen: colored nutrient sub-cards in the Meal Totals,
 * per-food breakdown with safety pills and big icon tiles, swaps with
 * impact-reduction badges. The funnel feels like the product.
 *
 * Shows the full Pro analysis (Cam, 2026-10-01: every buyer starts on Pro, so
 * show what Pro gets): the app's "More nutrients" for the meal and each food,
 * Smart Swaps and the Pro Tip.
 */

/** Pro nutrients beyond the kidney four (the app's "More nutrients"). Absent = unknown. */
export interface ExtraNutrients {
  calories?: number
  carbsG?: number
  sugarsG?: number
  fiberG?: number
  fatG?: number
  saturatedFatG?: number
  calciumMg?: number
  magnesiumMg?: number
  ironMg?: number
  vitaminKMcg?: number
  /** Kidney-stone tracking (display-only in the app too). */
  oxalateMg?: number
}

export interface AnalysisFood extends ExtraNutrients {
  name: string
  portion?: string
  emoji?: string
  sodiumMg: number
  potassiumMg: number
  phosphorusMg: number
  proteinG: number
  safetyLevel: 'safe' | 'moderate' | 'limit' | 'high'
  warning?: string | null
}

export interface AnalysisSwap {
  original: string
  suggestion: string
  /** "X% less potassium" style label */
  impactLabel?: string
  /** "Easy swap" / "Worth it" style pill */
  difficulty?: string
  reason?: string
}

export interface AnalysisResult {
  score: number
  overallRisk: 'safe' | 'moderate' | 'high'
  foods: AnalysisFood[]
  totals: {
    sodiumMg: number
    potassiumMg: number
    phosphorusMg: number
    proteinG: number
  } & ExtraNutrients
  swaps: AnalysisSwap[]
  reasoning?: string
  /** The analysis's Pro Tip (cloud response `suggestions[0]`). */
  tip?: string
}

export interface AnalysisLimits {
  sodium: number
  potassium: number
  phosphorus: number
  protein: number
}

interface Props {
  result: AnalysisResult
  limits: AnalysisLimits
  mealPhotoUrl?: string
  sourceLabel?: string
  onContinue: () => void
  continueLabel?: string
  onTryAnother?: () => void
  tryAnotherLabel?: string
}

export function MealAnalysisResult({
  result,
  limits,
  mealPhotoUrl,
  sourceLabel,
  onContinue,
  continueLabel = 'Continue',
  onTryAnother,
  tryAnotherLabel = 'Try Another',
}: Props) {
  // Derive the risk label DIRECTLY from the score using the same thresholds
  // shown in the legend (0-30 safe, 31-60 moderate, 61+ high). The AI's
  // separate `overallRisk` field is not trustworthy — it sometimes returns
  // a label that contradicts its own computed score (e.g. score 79 with
  // overallRisk='safe'). Score-derived ensures color/label always agree with
  // the number the user is staring at.
  const scoreRisk: 'safe' | 'moderate' | 'high' =
    result.score >= 61 ? 'high'
      : result.score >= 31 ? 'moderate'
      : 'safe'
  const riskMeta =
    scoreRisk === 'high'
      ? { color: '#EF4444', label: 'High Burden', badgeBg: '#EF4444', badgeText: 'High kidney burden' }
      : scoreRisk === 'moderate'
      ? { color: '#F59E0B', label: 'Moderate', badgeBg: '#F59E0B', badgeText: 'Moderate' }
      : { color: '#22C55E', label: 'Kidney Safe', badgeBg: '#22C55E', badgeText: 'Kidney safe' }

  const pct = (value: number, limit: number) =>
    limit > 0 ? Math.min(100, Math.round((value / limit) * 100)) : 0

  return (
    <div className="w-full">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 text-center">
        Your Kidney Analysis
      </h1>
      {sourceLabel && (
        <p className="text-sm text-gray-500 text-center mt-1 mb-5">{sourceLabel}</p>
      )}
      {!sourceLabel && <div className="mb-5" />}

      {/* Meal photo (only when we have one — user's photo or canned demo) */}
      {mealPhotoUrl && (
        <div className="relative rounded-2xl overflow-hidden mb-4 shadow-sm">
          <img src={mealPhotoUrl} alt="" className="w-full aspect-[16/10] object-cover" />
          <div
            className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white text-xs font-semibold px-3 py-1.5 rounded-full"
            style={{ backgroundColor: riskMeta.badgeBg }}
          >
            {result.overallRisk === 'high' && (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
              </svg>
            )}
            {riskMeta.badgeText}
          </div>
        </div>
      )}

      {/* Score card — reasoning lives INSIDE the card (app pattern) */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4 shadow-sm">
        <div className="flex items-center gap-4 mb-3">
          <div
            className="w-16 h-16 rounded-full border-4 flex items-center justify-center shrink-0"
            style={{ borderColor: riskMeta.color }}
          >
            <span className="text-2xl font-bold" style={{ color: riskMeta.color }}>
              {result.score}
            </span>
          </div>
          <div>
            <p className="font-bold text-lg" style={{ color: riskMeta.color }}>
              {riskMeta.label}
            </p>
            <p className="text-sm text-gray-500">Kidney Score</p>
          </div>
        </div>

        {result.reasoning && (
          <p className="text-sm text-gray-700 leading-relaxed">{result.reasoning}</p>
        )}
      </div>

      {/* Score legend — below the card, with colored dots */}
      <div className="flex justify-center gap-6 mb-5">
        <LegendDot color="#22C55E" label="Safe" range="0-30" />
        <LegendDot color="#F59E0B" label="Moderate" range="31-60" />
        <LegendDot color="#EF4444" label="High" range="61+" />
      </div>

      {/* Meal Totals — colored nutrient sub-cards in a 2x2 grid */}
      <div className="rounded-2xl border-2 border-primary p-4 mb-4" style={{ backgroundColor: 'rgba(20, 184, 166, 0.04)' }}>
        <h3 className="text-center font-semibold text-gray-900 mb-3">Meal Totals</h3>
        <div className="grid grid-cols-2 gap-3">
          <NutrientCard
            tint="#FEF3C7"
            iconBg="#FDE68A"
            iconSrc="/images/nutrients/sodium.png"
            label="Sodium"
            value={Math.round(result.totals.sodiumMg)}
            unit="mg"
            pctValue={pct(result.totals.sodiumMg, limits.sodium)}
            barColor="#F59E0B"
          />
          <NutrientCard
            tint="#D1FAE5"
            iconBg="#A7F3D0"
            iconSrc="/images/nutrients/potassium.png"
            label="Potassium"
            value={Math.round(result.totals.potassiumMg)}
            unit="mg"
            pctValue={pct(result.totals.potassiumMg, limits.potassium)}
            barColor="#22C55E"
          />
          <NutrientCard
            tint="#E9D5FF"
            iconBg="#DDD6FE"
            iconSrc="/images/nutrients/phosphorus.png"
            label="Phosphorus"
            value={Math.round(result.totals.phosphorusMg)}
            unit="mg"
            pctValue={pct(result.totals.phosphorusMg, limits.phosphorus)}
            barColor="#8B5CF6"
          />
          <NutrientCard
            tint="#DBEAFE"
            iconBg="#BFDBFE"
            iconSrc="/images/nutrients/protein.png"
            label="Protein"
            value={formatProtein(result.totals.proteinG)}
            unit="g"
            pctValue={pct(result.totals.proteinG, limits.protein)}
            barColor="#3B82F6"
          />
        </div>
        {hasExtras(result.totals) && (
          <>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-4 mb-2">More nutrients</p>
            <MoreNutrients n={result.totals} />
          </>
        )}
        <p className="text-[11px] text-gray-500 text-center mt-3 italic">
          Values are AI estimates and may vary
        </p>
      </div>

      {/* Identified Foods */}
      {result.foods.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <V2Icon name="section-foods" size={24} />
              <h3 className="font-semibold text-gray-900">Identified Foods</h3>
            </div>
            <span className="bg-amber-100 text-amber-700 text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
              {result.foods.length}
            </span>
          </div>

          <div className="space-y-4">
            {result.foods.map((food, i) => (
              <FoodItem key={i} food={food} />
            ))}
          </div>
        </div>
      )}

      {/* Smart Swaps */}
      {result.swaps.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <V2Icon name="section-swaps" size={24} />
              <h3 className="font-semibold text-gray-900">Smart Swaps</h3>
            </div>
            <span className="bg-teal-50 text-primary text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
              {result.swaps.length}
            </span>
          </div>

          <div className="space-y-4">
            {result.swaps.map((swap, i) => (
              <SwapItem key={i} swap={swap} last={i === result.swaps.length - 1} />
            ))}
          </div>
        </div>
      )}

      {/* Pro Tip */}
      {result.tip && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <V2Icon name="section-tip" size={24} />
            <h3 className="font-semibold text-gray-900">Pro Tip</h3>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">{result.tip}</p>
        </div>
      )}
      <div className="mb-2" />

      {/* CTA buttons */}
      <Button onClick={onContinue}>
        <span className="flex items-center justify-center gap-2">
          {continueLabel}
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </span>
      </Button>

      {onTryAnother && (
        <button
          onClick={onTryAnother}
          className="w-full mt-3 py-3 rounded-full border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
        >
          {tryAnotherLabel}
        </button>
      )}
    </div>
  )
}

// ────────────────────────── Sub-components ──────────────────────────

function LegendDot({ color, label, range }: { color: string; label: string; range: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="w-2 h-2 rounded-full mb-1" style={{ backgroundColor: color }} />
      <span className="text-xs font-semibold text-gray-700">{label}</span>
      <span className="text-[10px] text-gray-400">{range}</span>
    </div>
  )
}

function NutrientCard({
  tint,
  iconBg,
  iconSrc,
  label,
  value,
  unit,
  pctValue,
  barColor,
}: {
  tint: string
  iconBg: string
  iconSrc: string
  label: string
  value: number | string
  unit: string
  pctValue: number
  barColor: string
}) {
  return (
    <div className="rounded-2xl p-3" style={{ backgroundColor: tint }}>
      <div className="flex items-start justify-between mb-2">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: iconBg }}>
          <img src={iconSrc} alt="" className="w-7 h-7 object-contain" />
        </div>
        <span className="text-xs text-gray-700 font-medium pt-1.5">{label}</span>
      </div>
      <div className="flex items-baseline gap-0.5 mb-1.5">
        <span className="text-2xl font-bold text-gray-900">{value}</span>
        <span className="text-xs text-gray-500 font-medium">{unit}</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-white/60 overflow-hidden mb-1">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pctValue}%`, backgroundColor: barColor }}
        />
      </div>
      <p className="text-[10px] text-gray-600 font-semibold">{pctValue}% DV</p>
    </div>
  )
}

function FoodItem({ food }: { food: AnalysisFood }) {
  const [showMore, setShowMore] = useState(false)
  const safety = normalizeSafety(food.safetyLevel)
  const dotColor =
    safety === 'limit' ? '#EF4444' : safety === 'moderate' ? '#F59E0B' : '#22C55E'
  const badgeColor =
    safety === 'limit'
      ? { bg: '#FEE2E2', text: '#DC2626' }
      : safety === 'moderate'
      ? { bg: '#FEF3C7', text: '#D97706' }
      : { bg: '#D1FAE5', text: '#059669' }
  const badgeLabel = safety === 'limit' ? 'Limit' : safety === 'moderate' ? 'Moderate' : 'Safe'

  return (
    <div className="border border-gray-100 rounded-xl p-3">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 truncate">
              {food.emoji && <span className="mr-1">{food.emoji}</span>}
              {food.name}
            </p>
            {food.portion && <p className="text-xs text-gray-400">{food.portion}</p>}
          </div>
        </div>
        <span
          className="text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ml-2 flex items-center gap-1"
          style={{ color: badgeColor.text, backgroundColor: badgeColor.bg }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: badgeColor.text }} />
          {badgeLabel}
        </span>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <FoodNutrient tint="#FEF3C7" iconSrc="/images/nutrients/sodium.png" label="SODIUM" value={`${Math.round(food.sodiumMg)}mg`} />
        <FoodNutrient tint="#D1FAE5" iconSrc="/images/nutrients/potassium.png" label="POTASSIUM" value={`${Math.round(food.potassiumMg)}mg`} />
        <FoodNutrient tint="#E9D5FF" iconSrc="/images/nutrients/phosphorus.png" label="PHOSPHORUS" value={`${Math.round(food.phosphorusMg)}mg`} />
        <FoodNutrient tint="#DBEAFE" iconSrc="/images/nutrients/protein.png" label="PROTEIN" value={`${formatProtein(food.proteinG)}g`} />
      </div>

      {hasExtras(food) && (
        <>
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            className="w-full mt-3 text-xs font-semibold text-gray-500 flex items-center justify-center gap-1"
          >
            {showMore ? 'Less' : 'More nutrients'}
            <svg className={`w-3.5 h-3.5 transition-transform ${showMore ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showMore && <div className="mt-2"><MoreNutrients n={food} compact /></div>}
        </>
      )}

      {food.warning && (
        <div className="mt-3 inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-md">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 11a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          {food.warning}
        </div>
      )}
    </div>
  )
}

function FoodNutrient({ tint, iconSrc, label, value }: { tint: string; iconSrc: string; label: string; value: string }) {
  return (
    <div className="text-center">
      <div className="w-9 h-9 rounded-lg mx-auto flex items-center justify-center mb-1" style={{ backgroundColor: tint }}>
        <img src={iconSrc} alt="" className="w-6 h-6 object-contain" />
      </div>
      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">{label}</p>
      <p className="font-bold text-gray-900 text-xs mt-0.5">{value}</p>
    </div>
  )
}

function SwapItem({ swap, last }: { swap: AnalysisSwap; last: boolean }) {
  return (
    <div className={`${last ? '' : 'border-b border-gray-100 pb-4'}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-gray-400 line-through text-sm">{swap.original}</span>
        <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </div>
      <p className="text-primary font-semibold text-base mb-2">{swap.suggestion}</p>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {swap.impactLabel && (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-md">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            {swap.impactLabel}
          </span>
        )}
        {swap.difficulty && (
          <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-md">
            {swap.difficulty}
          </span>
        )}
      </div>
      {swap.reason && (
        <p className="text-xs text-gray-500 leading-relaxed">{swap.reason}</p>
      )}
    </div>
  )
}

/** The app's "More nutrients" tiles, for whichever values the analysis returned. */
function MoreNutrients({ n, compact = false }: { n: ExtraNutrients; compact?: boolean }) {
  const tiles: Array<{ icon: string | null; label: string; value: string; sub?: string }> = []
  const g = (v: number) => (v >= 10 ? String(Math.round(v)) : v.toFixed(1).replace(/\.0$/, ''))
  if (isNum(n.calories)) tiles.push({ icon: 'calories', label: 'Calories', value: `${Math.round(n.calories)} kcal` })
  if (isNum(n.carbsG)) {
    const sub = [isNum(n.sugarsG) ? `Sugar ${g(n.sugarsG)}g` : null, isNum(n.fiberG) ? `Fiber ${g(n.fiberG)}g` : null].filter(Boolean).join(' · ')
    tiles.push({ icon: 'carb', label: 'Carbs', value: `${g(n.carbsG)}g`, sub: sub || undefined })
  }
  if (isNum(n.fatG)) tiles.push({ icon: 'fat', label: 'Fat', value: `${g(n.fatG)}g`, sub: isNum(n.saturatedFatG) ? `Sat. fat ${g(n.saturatedFatG)}g` : undefined })
  if (isNum(n.calciumMg)) tiles.push({ icon: 'calcium', label: 'Calcium', value: `${Math.round(n.calciumMg)}mg` })
  if (isNum(n.magnesiumMg)) tiles.push({ icon: 'magnesium', label: 'Magnesium', value: `${Math.round(n.magnesiumMg)}mg` })
  if (isNum(n.ironMg)) tiles.push({ icon: 'iron', label: 'Iron', value: `${g(n.ironMg)}mg` })
  if (isNum(n.vitaminKMcg)) tiles.push({ icon: 'vitamink', label: 'Vitamin K', value: `${Math.round(n.vitaminKMcg)}mcg` })
  if (isNum(n.oxalateMg)) tiles.push({ icon: 'oxalate', label: 'Oxalate', value: `${Math.round(n.oxalateMg)}mg` })
  return (
    <div className={`grid ${compact ? 'grid-cols-4 gap-2' : 'grid-cols-3 gap-2'}`}>
      {tiles.map((t) => (
        <div key={t.label} className={`rounded-xl bg-white border border-gray-100 text-center ${compact ? 'p-1.5' : 'p-2'}`}>
          {t.icon && <img src={`/images/nutrients/${t.icon}.png`} alt="" className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} object-contain mx-auto mb-1`} />}
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">{t.label}</p>
          <p className={`font-bold text-gray-900 ${compact ? 'text-[11px]' : 'text-sm'}`}>{t.value}</p>
          {t.sub && !compact && <p className="text-[10px] text-gray-500 leading-tight mt-0.5">{t.sub}</p>}
        </div>
      ))}
    </div>
  )
}

// ────────────────────────── Helpers ──────────────────────────

function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v)
}

function hasExtras(n: ExtraNutrients): boolean {
  return [n.calories, n.carbsG, n.fatG, n.calciumMg, n.magnesiumMg, n.ironMg, n.vitaminKMcg, n.oxalateMg].some(isNum)
}

function normalizeSafety(s: AnalysisFood['safetyLevel']): 'safe' | 'moderate' | 'limit' {
  if (s === 'high' || s === 'limit') return 'limit'
  if (s === 'moderate') return 'moderate'
  return 'safe'
}

function formatProtein(g: number): string {
  if (g % 1 === 0) return String(Math.round(g))
  return g.toFixed(1)
}
